import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const DATA_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

const SMART_MONEY_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/smart_money.json";

const STOCK_DETAIL_PATH = "/stocks2";

const SCORE_CONFIG = {
  quality: [
    { key: "buybackYield", label: "Buyback Yield", min: -0.1, max: 0.1, direction: "higher", allowNegative: true, weight: 0.15 },
    { key: "growthRate", label: "Growth Rate", min: -0.2, max: 0.2, direction: "higher", allowNegative: true, weight: 0.2 },
    { key: "grossMargin", label: "Gross Margin", min: 0, max: 1, direction: "higher", allowNegative: true, weight: 0.1 },
    { key: "profitMargin", label: "Profit Margin", min: -0.1, max: 0.3, direction: "higher", allowNegative: true, weight: 0.2 },
    { key: "currentRatio", label: "Current Ratio", min: 0.01, max: 3, direction: "higher", allowNegative: false, weight: 0.15 },
    { key: "institutionalOwnership", label: "Inst. Ownership", min: 0.5, max: 1, direction: "higher", allowNegative: false, weight: 0.2 },
  ],
  valuation: [
    { key: "pb", label: "P/B", min: 0.01, max: 10, direction: "lower", allowNegative: false, weight: 0.2 },
    { key: "ptbv", label: "P/TBV", min: 0.01, max: 10, direction: "lower", allowNegative: false, weight: 0.2 },
    { key: "peg", label: "PEG", min: 0.01, max: 4, direction: "lower", allowNegative: false, weight: 0.2 },
    { key: "forwardPE", label: "Forward P/E", min: 0.01, max: 50, direction: "lower", allowNegative: false, weight: 0.2 },
    { key: "evMarketCap", label: "EV / Market Cap", min: 0.01, max: 2, direction: "lower", allowNegative: false, weight: 0.2 },
  ],
  scale: [
    { key: "rsi14", label: "RSI", min: 15, max: 70, direction: "lower", allowNegative: false,weight:0.3 },
    { key: "pctAbove52WeekLow", label: "Distance From 52W Low", min: 0, max: 0.5, direction: "lower", allowNegative: false ,weight:0.7},
  ],
  defensive: [
    { key: "beta", label: "Beta", min: 0, max: 2, direction: "lower", allowNegative: true },
    { key: "pctUpWhenSpyDown", label: "% Days Up When SPY Down", min: 0, max: 0.5, direction: "higher", allowNegative: false },
    { key: "pctUpWhenSpyCrash", label: "% Days Up When SPY Down > 1.5%", min: 0, max: 0.35, direction: "higher", allowNegative: false },
  ],
  smartMoney: [
    { key: "smartMoneyScoreRaw", label: "Smart Money", min: 0, max: 100, direction: "higher", allowNegative: true, weight: 1 },
  ],
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,%]/g, "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeMetricValue(rawValue, config) {
  const n = parseNumber(rawValue);
  if (!Number.isFinite(n)) return null;
  if (!config.allowNegative && n <= 0) return null;

  const capped = clamp(n, config.min, config.max);
  const range = config.max - config.min;
  if (range <= 0) return null;

  if (config.direction === "lower") {
    return ((config.max - capped) / range) * 100;
  }

  return ((capped - config.min) / range) * 100;
}

function computeScore(row, configs) {
  const details = configs.map((config) => {
    let rawValue = row[config.key];

    if (config.key === "evMarketCap") {
      const ev = parseNumber(row.enterpriseValue);
      const marketCap = parseNumber(row.marketCap);
      rawValue =
        Number.isFinite(ev) && Number.isFinite(marketCap) && marketCap > 0
          ? ev / marketCap
          : null;
    }

    const metricScore = normalizeMetricValue(rawValue, config);
    const baseWeight = Number.isFinite(Number(config.weight)) ? Number(config.weight) : 1;

    return {
      key: config.key,
      label: config.label,
      rawValue,
      score: metricScore,
      baseWeight,
      isValid: Number.isFinite(metricScore),
    };
  });

  const valid = details.filter((x) => x.isValid);
  const validWeight = valid.reduce((sum, x) => sum + x.baseWeight, 0);

  if (!valid.length || validWeight <= 0) {
    return {
      score: null,
      validCount: 0,
      possibleCount: configs.length,
      details,
    };
  }

  const score = valid.reduce(
    (sum, x) => sum + x.score * (x.baseWeight / validWeight),
    0
  );

  return {
    score,
    validCount: valid.length,
    possibleCount: configs.length,
    details: details.map((x) => ({
      ...x,
      effectiveWeight: x.isValid ? x.baseWeight / validWeight : 0,
    })),
  };
}

function formatScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(0);
}

function formatInteger(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(0);
}

function formatRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatLargeMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  const abs = Math.abs(n);
  if (abs >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
}

function scoreColor(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) {
    return { color: "#64748b", background: "#f8fafc", borderColor: "#e2e8f0" };
  }
  if (n >= 80) {
    return { color: "#047857", background: "#ecfdf5", borderColor: "#a7f3d0" };
  }
  if (n >= 65) {
    return { color: "#15803d", background: "#f0fdf4", borderColor: "#bbf7d0" };
  }
  if (n >= 50) {
    return { color: "#a16207", background: "#fefce8", borderColor: "#fde68a" };
  }
  return { color: "#b91c1c", background: "#fff1f2", borderColor: "#fecdd3" };
}

function ScorePill({ score }) {
  return (
    <span style={{ ...scorePill, ...scoreColor(score) }}>
      {formatScore(score)}
    </span>
  );
}


function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < String(line || "").length; i++) {
    const ch = String(line || "")[i];
    const next = String(line || "")[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function isLikelyEtf(row) {
  const ticker = String(row?.ticker || "").trim().toUpperCase();
  const name = String(row?.name || "").toLowerCase();

  const etfTickers = new Set([
    "SPY", "QQQ", "IWM", "DIA", "XLE", "XLF", "XLK", "XLV", "XLI", "XLP",
    "XLU", "XLY", "ARKK", "VTI", "VOO", "SCHD", "VEA", "EEM", "TLT", "IEF",
    "HYG", "JNK", "BIL", "GLD", "DBC", "XME", "COPX", "PSQ", "SH", "FBTC", "VXX",
  ]);

  if (etfTickers.has(ticker)) return true;

  return (
    name.includes(" etf") ||
    name.includes("exchange traded") ||
    name.includes("fund") ||
    name.includes("trust") ||
    name.includes("ishares") ||
    name.includes("spdr") ||
    name.includes("vanguard") ||
    name.includes("invesco") ||
    name.includes("proshares") ||
    name.includes("direxion")
  );
}

function getSortValue(row, key) {
  if (key === "qualityScore") return row.qualityScore;
  if (key === "valuationScore") return row.valuationScore;
  if (key === "scaleScore") return row.scaleScore;
  if (key === "defensiveScore") return row.defensiveScore;
  if (key === "smartMoneyScore") return row.smartMoneyScore;
  if (key === "combinedScore") return row.combinedScore;
  return row[key];
}

const desktopColumns = [
  { key: "rank", label: "#", align: "right", width: 58 },
  { key: "ticker", label: "Ticker", width: 92, sticky: true, left: 0 },
  { key: "name", label: "Name", width: 230 },
  { key: "price", label: "Price", type: "money", align: "right", width: 90 },
  { key: "marketCap", label: "Market Cap", type: "moneyLarge", align: "right", width: 125 },
  { key: "qualityScore", label: "Quality", type: "score", align: "right", width: 95 },
  { key: "valuationScore", label: "Valuation", type: "score", align: "right", width: 108 },
  { key: "scaleScore", label: "Sale", type: "score", align: "right", width: 86 },
  { key: "defensiveScore", label: "Defensive", type: "score", align: "right", width: 104 },
  { key: "smartMoneyScore", label: "Smart Money", type: "score", align: "right", width: 118 },
  { key: "combinedScore", label: "Combined", type: "score", align: "right", width: 105 },
  { key: "qualityCount", label: "Q Valid", align: "right", width: 82 },
  { key: "valuationCount", label: "V Valid", align: "right", width: 82 },
  { key: "scaleCount", label: "S Valid", align: "right", width: 82 },
  { key: "defensiveCount", label: "D Valid", align: "right", width: 82 },
  { key: "smartMoneyCount", label: "SM Valid", align: "right", width: 86 },
  { key: "numFundsBuying", label: "Funds Buying", type: "integer", align: "right", width: 108 },
  { key: "numNewPositions", label: "New Pos.", type: "integer", align: "right", width: 92 },
  { key: "numFundsSelling", label: "Funds Selling", type: "integer", align: "right", width: 108 },
  { key: "peg", label: "PEG", type: "ratio", align: "right", width: 76 },
  { key: "pb", label: "P/B", type: "ratio", align: "right", width: 76 },
  { key: "ptbv", label: "P/TBV", type: "ratio", align: "right", width: 78 },
  { key: "forwardPE", label: "Fwd P/E", type: "ratio", align: "right", width: 86 },
  { key: "currentRatio", label: "Current", type: "ratio", align: "right", width: 86 },
  { key: "evMarketCap", label: "EV/MC", type: "ratio", align: "right", width: 82 },
  { key: "institutionalOwnership", label: "Inst Own", type: "percent", align: "right", width: 96 },
  { key: "buybackYield", label: "Buyback", type: "percent", align: "right", width: 92 },
  { key: "growthRate", label: "Growth", type: "percent", align: "right", width: 86 },
  { key: "grossMargin", label: "Gross Mgn", type: "percent", align: "right", width: 98 },
  { key: "profitMargin", label: "Profit Mgn", type: "percent", align: "right", width: 98 },
  { key: "rsi14", label: "RSI", type: "ratio", align: "right", width: 70 },
  { key: "pctAbove52WeekLow", label: "% Above Low", type: "percent", align: "right", width: 108 },
  { key: "beta", label: "Beta", type: "ratio", align: "right", width: 70 },
  { key: "pctUpWhenSpyDown", label: "% Up SPY Down", type: "percent", align: "right", width: 120 },
  { key: "pctUpWhenSpyCrash", label: "% Up Crash", type: "percent", align: "right", width: 105 },
];

const mobileColumns = [
  { key: "rank", label: "#", align: "right", width: 46 },
  { key: "ticker", label: "Ticker", width: 82, sticky: true, left: 0 },
  { key: "qualityScore", label: "Quality", type: "score", align: "right", width: 84 },
  { key: "valuationScore", label: "Value", type: "score", align: "right", width: 84 },
  { key: "scaleScore", label: "Scale", type: "score", align: "right", width: 74 },
  { key: "defensiveScore", label: "Def", type: "score", align: "right", width: 72 },
  { key: "smartMoneyScore", label: "Smart", type: "score", align: "right", width: 78 },
  { key: "combinedScore", label: "Combined", type: "score", align: "right", width: 92 },
  { key: "numFundsBuying", label: "Funds", type: "integer", align: "right", width: 72 },
  { key: "marketCap", label: "Mkt Cap", type: "moneyLarge", align: "right", width: 106 },
  { key: "forwardPE", label: "Fwd P/E", type: "ratio", align: "right", width: 82 },
  { key: "pb", label: "P/B", type: "ratio", align: "right", width: 66 },
  { key: "rsi14", label: "RSI", type: "ratio", align: "right", width: 64 },
  { key: "pctAbove52WeekLow", label: "% Low", type: "percent", align: "right", width: 88 },
  { key: "beta", label: "Beta", type: "ratio", align: "right", width: 64 },
  { key: "pctUpWhenSpyDown", label: "SPY Down", type: "percent", align: "right", width: 88 },
  { key: "pctUpWhenSpyCrash", label: "Crash", type: "percent", align: "right", width: 82 },
];

export default function StockRankingsPage() {
  const isMobile = useIsMobile(768);

  const [rows, setRows] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [spyStats, setSpyStats] = useState({});
  const [smartMoneyData, setSmartMoneyData] = useState({});
  const [smartMoneyAsOf, setSmartMoneyAsOf] = useState(null);

  const [search, setSearch] = useState("");
  const [minMarketCap, setMinMarketCap] = useState("");
  const [maxMarketCap, setMaxMarketCap] = useState("");
  const [sortKey, setSortKey] = useState("combinedScore");
  const [sortDir, setSortDir] = useState("desc");

  const visibleColumns = isMobile ? mobileColumns : desktopColumns;

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(DATA_URL, { cache: "no-store" });
        const text = await res.text();

        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        if (text.trim().startsWith("<")) {
          throw new Error("Expected JSON but received HTML. Check the data URL/path.");
        }

        const json = JSON.parse(text);
        setRows(Array.isArray(json.data) ? json.data : []);
        setAsOf(json.as_of || null);
      } catch (e) {
        console.error(e);
        setError(e.message || "Failed to load ranking data.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);


  useEffect(() => {
    async function loadSmartMoneyData() {
      try {
        const res = await fetch(SMART_MONEY_URL, { cache: "no-store" });
        const text = await res.text();

        if (!res.ok || text.trim().startsWith("<")) return;

        const json = JSON.parse(text);
        const rawData = json?.data && typeof json.data === "object" ? json.data : {};

        const normalized = {};
        for (const [ticker, value] of Object.entries(rawData)) {
          const cleanTicker = String(ticker || "").trim().toUpperCase();
          if (cleanTicker) normalized[cleanTicker] = value;
        }

        setSmartMoneyData(normalized);
        setSmartMoneyAsOf(json?.as_of || null);
      } catch (err) {
        console.error(err);
      }
    }

    loadSmartMoneyData();
  }, []);


  useEffect(() => {
    async function loadSpyDownStats() {
      try {
        const res = await fetch(RETURNS_URL, { cache: "no-store" });
        const csv = await res.text();

        if (!res.ok || csv.trim().startsWith("<")) return;

        const lines = csv.trim().split(/\r?\n/);
        if (lines.length < 2) return;

        const headers = parseCsvLine(lines[0]).map((h) =>
          String(h || "").trim().toUpperCase()
        );

        const spyIndex = headers.indexOf("SPY");
        if (spyIndex === -1) return;

        const stockTickers = new Set(
          rows
            .filter((row) => !isLikelyEtf(row))
            .map((row) => String(row.ticker || "").trim().toUpperCase())
            .filter(Boolean)
        );

        const tickerIndexes = headers
          .map((ticker, index) => ({ ticker, index }))
          .filter((x) => x.index !== spyIndex && stockTickers.has(x.ticker));

        const stats = {};
        for (const { ticker } of tickerIndexes) {
          stats[ticker] = {
            spyDown: 0,
            stockUpWhenSpyDown: 0,
            spyCrash: 0,
            stockUpWhenSpyCrash: 0,
          };
        }

        for (let r = 1; r < lines.length; r++) {
          const cols = parseCsvLine(lines[r]);
          const spy = Number(cols[spyIndex]) / 100;

          if (!Number.isFinite(spy)) continue;

          const isSpyDown = spy < 0;
          const isSpyCrash = spy <= -0.015;

          if (!isSpyDown && !isSpyCrash) continue;

          for (const { ticker, index } of tickerIndexes) {
            const stock = Number(cols[index]) / 100;
            if (!Number.isFinite(stock)) continue;

            if (isSpyDown) {
              stats[ticker].spyDown++;
              if (stock > 0) stats[ticker].stockUpWhenSpyDown++;
            }

            if (isSpyCrash) {
              stats[ticker].spyCrash++;
              if (stock > 0) stats[ticker].stockUpWhenSpyCrash++;
            }
          }
        }

        const finalStats = {};
        for (const [ticker, s] of Object.entries(stats)) {
          finalStats[ticker] = {
            pctUpWhenSpyDown:
              s.spyDown > 0 ? s.stockUpWhenSpyDown / s.spyDown : null,
            pctUpWhenSpyCrash:
              s.spyCrash > 0 ? s.stockUpWhenSpyCrash / s.spyCrash : null,
          };
        }

        setSpyStats(finalStats);
      } catch (err) {
        console.error(err);
      }
    }

    if (rows.length) loadSpyDownStats();
  }, [rows]);

  const scoredRows = useMemo(() => {
    return rows
      .filter((row) => !isLikelyEtf(row))
      .map((row) => {
        const enterpriseValue = parseNumber(row.enterpriseValue);
        const marketCap = parseNumber(row.marketCap);

        const evMarketCap =
          Number.isFinite(enterpriseValue) &&
          Number.isFinite(marketCap) &&
          marketCap > 0
            ? enterpriseValue / marketCap
            : null;

        const ticker = String(row.ticker || "").trim().toUpperCase();

        const smartMoney = smartMoneyData?.[ticker] || {};
        const smartMoneyScoreRaw = parseNumber(smartMoney.smartMoneyScore);

        const enriched = {
          ...row,
          evMarketCap,
          smartMoneyScoreRaw,
          smartMoneyScore: smartMoneyScoreRaw,
          numFundsBuying: parseNumber(smartMoney.numFundsBuying),
          numNewPositions: parseNumber(smartMoney.numNewPositions),
          numFundsSelling: parseNumber(smartMoney.numFundsSelling),
          totalSmartMoneyValueAddedUsd: parseNumber(smartMoney.totalValueAddedUsd),
          pctUpWhenSpyDown: spyStats?.[ticker]?.pctUpWhenSpyDown ?? null,
          pctUpWhenSpyCrash: spyStats?.[ticker]?.pctUpWhenSpyCrash ?? null,
        };

        const quality = computeScore(enriched, SCORE_CONFIG.quality);
        const valuation = computeScore(enriched, SCORE_CONFIG.valuation);
        const scale = computeScore(enriched, SCORE_CONFIG.scale);
        const defensive = computeScore(enriched, SCORE_CONFIG.defensive);
        const smartMoneyScoreObj = computeScore(enriched, SCORE_CONFIG.smartMoney);

        const weightedScores = [];

        if (Number.isFinite(quality.score)) {
          weightedScores.push({
            score: quality.score,
            weight: 0.35,
          });
        }

        if (Number.isFinite(valuation.score)) {
          weightedScores.push({
            score: valuation.score,
            weight: 0.25,
          });
        }

        if (Number.isFinite(scale.score)) {
          weightedScores.push({
            score: scale.score,
            weight: 0.12,
          });
        }

        if (Number.isFinite(defensive.score)) {
          weightedScores.push({
            score: defensive.score,
            weight: 0.18,
          });
        }

        if (Number.isFinite(smartMoneyScoreObj.score)) {
          weightedScores.push({
            score: smartMoneyScoreObj.score,
            weight: 0.1,
          });
        }

        const totalWeight = weightedScores.reduce(
          (sum, item) => sum + item.weight,
          0
        );

        const combinedScore =
          totalWeight > 0
            ? weightedScores.reduce(
                (sum, item) => sum + item.score * item.weight,
                0
              ) / totalWeight
            : null;

        return {
          ...enriched,
          qualityScore: quality.score,
          valuationScore: valuation.score,
          scaleScore: scale.score,
          defensiveScore: defensive.score,
          smartMoneyScore: smartMoneyScoreObj.score,
          combinedScore,
          qualityCount: `${quality.validCount}/${quality.possibleCount}`,
          valuationCount: `${valuation.validCount}/${valuation.possibleCount}`,
          scaleCount: `${scale.validCount}/${scale.possibleCount}`,
          defensiveCount: `${defensive.validCount}/${defensive.possibleCount}`,
          smartMoneyCount: `${smartMoneyScoreObj.validCount}/${smartMoneyScoreObj.possibleCount}`,
          qualityDetails: quality.details,
          valuationDetails: valuation.details,
          scaleDetails: scale.details,
          defensiveDetails: defensive.details,
          smartMoneyDetails: smartMoneyScoreObj.details,
        };
      })
      .filter(
        (row) =>
          Number.isFinite(row.qualityScore) ||
          Number.isFinite(row.valuationScore) ||
          Number.isFinite(row.scaleScore) ||
          Number.isFinite(row.defensiveScore) ||
          Number.isFinite(row.smartMoneyScore)
      );
  }, [rows, spyStats, smartMoneyData]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const data = scoredRows.filter((row) => {
      const ticker = String(row.ticker || "").toLowerCase();
      const name = String(row.name || "").toLowerCase();

      const matchesSearch =
        !q || ticker.includes(q) || name.includes(q);

      const marketCap = Number(row.marketCap);

      const passesMinMarketCap =
        !minMarketCap ||
        (Number.isFinite(marketCap) &&
          marketCap >= Number(minMarketCap) * 1_000_000_000);

      const passesMaxMarketCap =
        !maxMarketCap ||
        (Number.isFinite(marketCap) &&
          marketCap <= Number(maxMarketCap) * 1_000_000_000);

      return (
        matchesSearch &&
        passesMinMarketCap &&
        passesMaxMarketCap
      );
    });

    data.sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);

      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;

      const an = Number(av);
      const bn = Number(bv);

      if (Number.isFinite(an) && Number.isFinite(bn)) {
        return sortDir === "asc" ? an - bn : bn - an;
      }

      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });

    return data.map((row, index) => ({ ...row, rank: index + 1 }));
  }, [scoredRows, search, sortKey, sortDir, minMarketCap, maxMarketCap]);

  // const scoreSummary = useMemo(() => {
  //   const qualityScores = filteredRows.map((r) => r.qualityScore).filter(Number.isFinite);
  //   const scaleScores = filteredRows.map((r) => r.scaleScore).filter(Number.isFinite);
  //   const defensiveScores = filteredRows.map((r) => r.defensiveScore).filter(Number.isFinite);
  //   const combinedScores = filteredRows.map((r) => r.combinedScore).filter(Number.isFinite);

  //   const avg = (arr) =>
  //     arr.length ? arr.reduce((sum, value) => sum + value, 0) / arr.length : null;

  //   return {
  //     count: filteredRows.length,
  //     avgQuality: avg(qualityScores),
  //     avgScale: avg(scaleScores),
  //     avgDefensive: avg(defensiveScores),
  //     avgCombined: avg(combinedScores),
  //   };
  // }, [filteredRows]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <div style={pageShell}>
      <section style={heroCard}>
        <div>
          <div style={eyebrow}>Majorah Ranking Model</div>
          <h1 style={title}>Stock Quality, Sale Rankings</h1>
          <p style={subtitle}>
            {asOf ? ` Data as of ${asOf}.` : ""}
            {smartMoneyAsOf ? ` Smart money as of ${smartMoneyAsOf}.` : ""}
          </p>
        </div>

{/*        <div style={metricGrid}>
          <MetricCard label="Stocks Ranked" value={scoreSummary.count.toLocaleString()} />
          <MetricCard label="Avg Quality" value={formatScore(scoreSummary.avgQuality)} />
          <MetricCard label="Avg Sale" value={formatScore(scoreSummary.avgScale)} />
          <MetricCard label="Avg Defensive" value={formatScore(scoreSummary.avgDefensive)} />
          <MetricCard label="Avg Combined" value={formatScore(scoreSummary.avgCombined)} />
        </div>
*/}      </section>

      <section style={controlCard}>
        <div>
          <h2 style={sectionTitle}>Ranking Table</h2>
          <p style={sectionSubtitle}>
            Sort by quality, valuation, sale, defensive, smart money, combined score, or any raw factor.
          </p>
        </div>

        <div style={filterInputsRow}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticker or company..."
            style={searchInput}
          />

          <input
            value={minMarketCap}
            onChange={(e) => setMinMarketCap(e.target.value)}
            placeholder="Min Market Cap ($B)"
            type="number"
            style={smallFilterInput}
          />

          <input
            value={maxMarketCap}
            onChange={(e) => setMaxMarketCap(e.target.value)}
            placeholder="Max Market Cap ($B)"
            type="number"
            style={smallFilterInput}
          />
        </div>
      </section>

      {loading ? (
        <div style={message}>Loading ranking data...</div>
      ) : error ? (
        <div style={errorBox}>{error}</div>
      ) : (
        <section style={tableCard}>
          <div style={tableWrap}>
            <table style={{ ...table, minWidth: isMobile ? 1364 : 2960 }}>
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={getHeaderStyle(col)}
                    >
                      {col.label}
                      {sortKey === col.key ? (
                        <span style={sortIndicator}>{sortDir === "asc" ? " ▲" : " ▼"}</span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row, index) => (
                  <tr key={`${row.ticker}-${index}`} style={index % 2 === 0 ? rowEven : rowOdd}>
                    {visibleColumns.map((col) => (
                      <td key={col.key} style={getCellStyle(col)}>
                        {renderCell(row, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function renderCell(row, col) {
  if (col.key === "ticker") {
    const ticker = String(row.ticker || "").trim().toUpperCase();
    if (!ticker) return "—";

    return (
      <Link to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(ticker)}`} style={tickerLink}>
        {ticker}
      </Link>
    );
  }

  if (col.type === "score") return <ScorePill score={row[col.key]} />;

  if (
    col.key === "qualityCount" ||
    col.key === "valuationCount" ||
    col.key === "scaleCount" ||
    col.key === "defensiveCount" ||
    col.key === "smartMoneyCount"
  ) {
    return <span style={countPill}>{row[col.key]}</span>;
  }

  return formatValue(row[col.key], col.type);
}

function formatValue(value, type) {
  if (value === null || value === undefined || value === "" || Number.isNaN(value)) return "—";
  const n = Number(value);
  if (type === "moneyLarge") return formatLargeMoney(n);
  if (type === "money") return formatMoney(n);
  if (type === "percent") return formatPercent(n);
  if (type === "ratio") return formatRatio(n);
  if (type === "integer") return formatInteger(n);
  return String(value);
}

function getHeaderStyle(col) {
  const base = col.align === "right" ? thRight : thLeft;

  if (!col.sticky) {
    return { ...base, minWidth: col.width, width: col.width, maxWidth: col.width };
  }

  return {
    ...base,
    position: "sticky",
    left: col.left,
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
    zIndex: 7,
    background: "#f8fafc",
    boxShadow: "2px 0 0 rgba(148, 163, 184, 0.18)",
  };
}

function getCellStyle(col) {
  const base = col.align === "right" ? tdRight : td;

  if (!col.sticky) {
    return { ...base, minWidth: col.width, width: col.width, maxWidth: col.width };
  }

  return {
    ...base,
    position: "sticky",
    left: col.left,
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
    zIndex: 4,
    background: "#ffffff",
    boxShadow: "2px 0 0 rgba(226, 232, 240, 0.8)",
  };
}

// function MetricCard({ label, value }) {
//   return (
//     <div style={metricCard}>
//       <div style={metricLabel}>{label}</div>
//       <div style={metricValue}>{value}</div>
//     </div>
//   );
// }

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "22px 10px 34px",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 22,
  background:
    "radial-gradient(circle at 16% 12%, rgba(56, 189, 248, 0.23), transparent 32%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  borderRadius: 24,
  boxShadow: "0 22px 52px rgba(15, 23, 42, 0.2)",
};

const eyebrow = {
  marginBottom: 8,
  color: "#93c5fd",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 900,
};

const title = {
  margin: 0,
  fontSize: 32,
  letterSpacing: "-0.05em",
  lineHeight: 1.02,
};

const subtitle = {
  maxWidth: 980,
  marginTop: 10,
  color: "#cbd5e1",
  lineHeight: 1.65,
  fontSize: 14,
};

// const metricGrid = {
//   display: "grid",
//   gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
//   gap: 12,
//   marginTop: 22,
// };

// const metricCard = {
//   padding: 15,
//   borderRadius: 18,
//   background: "rgba(255, 255, 255, 0.1)",
//   border: "1px solid rgba(255, 255, 255, 0.16)",
//   boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
// };

// const metricLabel = {
//   fontSize: 10,
//   color: "#cbd5e1",
//   fontWeight: 850,
//   textTransform: "uppercase",
//   letterSpacing: "0.08em",
// };

// const metricValue = {
//   marginTop: 8,
//   fontSize: 22,
//   fontWeight: 950,
//   letterSpacing: "-0.04em",
// };

const controlCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "rgba(255, 255, 255, 0.9)",
  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
};

const sectionTitle = {
  margin: 0,
  fontSize: 17,
  letterSpacing: "-0.03em",
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: 12,
};

const filterInputsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-end",
  flex: "1 1 520px",
};

const searchInput = {
  minWidth: 260,
  flex: "1 1 260px",
  maxWidth: 420,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  fontSize: 12,
  background: "white",
  outline: "none",
};

const smallFilterInput = {
  width: 160,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  fontSize: 12,
  background: "white",
  outline: "none",
};

const tableCard = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.1)",
};

const tableWrap = {
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  maxHeight: "78vh",
};

const table = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const thLeft = {
  textAlign: "left",
  padding: "8px 8px",
  fontSize: 10,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  whiteSpace: "nowrap",
  cursor: "pointer",
  position: "sticky",
  top: 0,
  zIndex: 6,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const thRight = { ...thLeft, textAlign: "right" };

const td = {
  padding: "7px 8px",
  fontSize: 11,
  color: "#1e293b",
  borderBottom: "1px solid #eef2f7",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tdRight = {
  ...td,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const rowEven = { background: "#ffffff" };
const rowOdd = { background: "#fbfdff" };

const tickerLink = {
  color: "#2563eb",
  textDecoration: "none",
  fontWeight: 900,
};

const sortIndicator = {
  color: "#0f172a",
  fontSize: 10,
};

const scorePill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 46,
  padding: "5px 7px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 950,
  border: "1px solid transparent",
};

const countPill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 42,
  padding: "4px 7px",
  borderRadius: 999,
  color: "#475569",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  fontWeight: 850,
  fontSize: 10,
};

const message = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 20,
  color: "#475569",
  background: "white",
  borderRadius: 16,
  border: "1px solid #e2e8f0",
};

const errorBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 12,
};
